/**
 * Everything the app needs to know about *where* its data lives.
 *
 * The frontend reads one static artifact tree and nothing else — no API, no
 * auth, no server round-trip on the hot path (architecture §5). That tree has
 * exactly one address, and it is configured here.
 */

const trimSlash = (s: string): string => s.replace(/\/+$/, "");

/** Origin (optionally with a path prefix) of the published artifact tree. */
export const DATA_BASE_URL: string = trimSlash(
  import.meta.env.VITE_DATA_BASE_URL ?? "http://127.0.0.1:8787",
);

/** The identity registry. Lives under the tree unless told otherwise. */
export const ID_BASE_URL: string = trimSlash(
  import.meta.env.VITE_ID_BASE_URL ?? `${DATA_BASE_URL}/id`,
);

/** The bioregion's bounding box, [west, south, east, north]. Matches
 * `conditions.json.bbox` and `Settings.bbox` on the backend. */
export const FRONT_RANGE_BBOX: [number, number, number, number] = [
  -106.5, 38.5, -104.0, 41.0,
];

/** The camera cannot leave the bioregion plus a degree of context. */
const PAD = 1.0;
export const MAX_BOUNDS: [[number, number], [number, number]] = [
  [FRONT_RANGE_BBOX[0] - PAD, FRONT_RANGE_BBOX[1] - PAD],
  [FRONT_RANGE_BBOX[2] + PAD, FRONT_RANGE_BBOX[3] + PAD],
];

/** Opening shot: Boulder, looking north-north-west into the foothills. */
export const HOME_VIEW = {
  center: [-105.27, 40.02] as [number, number],
  zoom: 9,
  pitch: 55,
  bearing: -15,
};

/**
 * The landing page's shot: over Denver, tilted, looking north-west up the
 * range.
 *
 * Chosen against four alternatives by screenshotting all of them. Facing due
 * west puts the plains in the foreground and flattens the mountains into a
 * distant band; facing north from further out fills a third of the frame with
 * Wyoming. This bearing lays the Front Range diagonally across the picture with
 * the cities strung along its eastern foot — which is the shape of the thing
 * the whole site is about, and the low-angle relief shading rakes across it.
 *
 * `/front-range` is a plate and `/front-range/twin` is an instrument; the two want different cameras.
 */
export const LANDING_VIEW = {
  center: [-105.33, 39.83] as [number, number],
  zoom: 9.3,
  pitch: 73,
  bearing: -48,
};

/**
 * The same shot on a phone.
 *
 * A 375 px viewport is not the desktop frame cropped; it is a much narrower
 * field of view, and the wide camera above lands it squarely on the Denver
 * street grid — the least interesting square mile in the bioregion. Pulled
 * back and turned to put the range up the long axis of a tall screen.
 */
export const LANDING_VIEW_NARROW = {
  center: [-105.5, 40.05] as [number, number],
  zoom: 8.55,
  pitch: 70,
  bearing: -22,
};

/** Staleness thresholds, in seconds. PRD §7: the badge is always visible. */
export const FRESH_S = 10 * 60;
export const STALE_S = 60 * 60;

/** How often the conditions file is re-read. The publisher republishes every
 * five minutes; a minute of polling is cheap (one conditional GET) and keeps
 * the badge honest without hammering the CDN. */
export const REFRESH_MS = 60_000;
